import { useState, useEffect, useRef, useMemo } from 'react'
import type { ChangeEvent, DragEvent } from 'react'
import { Link } from 'react-router-dom'
import { buttonClassName } from '@/components/ui/buttonStyles'
import { usePageSEO } from '@/hooks/usePageSEO'
import { useTranslation } from 'react-i18next'
import LandingFooter from '@/components/landing/LandingFooter'
import PublicHeader from '@/components/layout/PublicHeader'
import { ToolFAQSection } from '@/components/tools/ToolFAQSection'
import { MoreTools, ToolBreadcrumb } from '@/components/tools/ToolPageParts'
import { TOOL_INPUT, TOOL_LABEL, TOOL_HINT } from '@/components/tools/toolUi'
import useFAQSchema from '@/hooks/useFAQSchema'
import { InstagramPreview, TikTokPreview, YouTubeShortsPreview } from '@/components/preview'
import { PLATFORM_LIMITS, PLATFORM_ORDER, type PlatformLimit } from '@/data/platformLimits'
import {
    ArrowRightIcon,
    ArrowUpTrayIcon,
    ExclamationTriangleIcon,
    FilmIcon,
} from '@heroicons/react/24/outline'

const BASE_URL = 'https://www.planwise.social'

/** Guard against the browser choking on a huge file — nothing is uploaded, but it is still decoded in-page. */
const MAX_FILE_BYTES = 100 * 1024 * 1024

type PreviewId = 'instagram' | 'tiktok' | 'shorts'

function formatFileSize(bytes: number, locale: string): string {
    const mb = bytes / (1024 * 1024)
    return mb >= 1
        ? `${mb.toLocaleString(locale, { maximumFractionDigits: 1 })} MB`
        : `${Math.max(1, Math.round(bytes / 1024)).toLocaleString(locale)} KB`
}

function parseHashtags(raw: string): string[] {
    return raw
        .split(/[\s,]+/)
        .map((tag) => tag.replace(/^#/, '').trim())
        .filter(Boolean)
}

export default function PostPreviewPage() {
    const { t, i18n } = useTranslation()
    const [mediaUrl, setMediaUrl] = useState<string | null>(null)
    const [mediaKind, setMediaKind] = useState<'image' | 'video'>('image')
    const [fileError, setFileError] = useState<string | null>(null)
    const [caption, setCaption] = useState('')
    const [hashtagInput, setHashtagInput] = useState('')
    const [username, setUsername] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const [dragging, setDragging] = useState(false)
    const [fileInfo, setFileInfo] = useState<{ name: string; size: number } | null>(null)
    // Phones show one preview at a time; three stacked 9:16 frames made the page
    // several screens long before the caption fields were visible again.
    const [activePreview, setActivePreview] = useState<PreviewId>('instagram')

    usePageSEO('toolsPostPreview', {
        canonicalPath: '/tools/post-preview',
        ogType: 'website',
        ogImage: `${BASE_URL}/og.jpg`,
        breadcrumbs: [{ name: 'Free Tools', path: '/tools' }, { name: t('tools.pages.postPreview.jsonLdName') }],
        jsonLd: {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'SoftwareApplication',
                    name: t('tools.pages.postPreview.jsonLdName'),
                    applicationCategory: 'BusinessApplication',
                    operatingSystem: 'Web',
                    url: `${BASE_URL}/tools/post-preview`,
                    description: t('tools.pages.postPreview.jsonLdDescription'),
                    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
                },
                {
                    '@type': 'HowTo',
                    name: t('tools.pages.postPreview.jsonLdName'),
                    description: t('tools.pages.postPreview.jsonLdDescription'),
                    step: [
                        { '@type': 'HowToStep', position: 1, text: 'Upload an image from your device.' },
                        { '@type': 'HowToStep', position: 2, text: 'Write your caption and hashtags.' },
                        { '@type': 'HowToStep', position: 3, text: 'Preview exactly how the post will look on Instagram, TikTok, and YouTube Shorts, including character limits.' },
                    ],
                },
            ],
        },
    })

    const faqs = [
        { question: t('tools.pages.postPreview.faq.q1.question'), answer: t('tools.pages.postPreview.faq.q1.answer') },
        { question: t('tools.pages.postPreview.faq.q2.question'), answer: t('tools.pages.postPreview.faq.q2.answer') },
        { question: t('tools.pages.postPreview.faq.q3.question'), answer: t('tools.pages.postPreview.faq.q3.answer') },
        { question: t('tools.pages.postPreview.faq.q4.question'), answer: t('tools.pages.postPreview.faq.q4.answer') },
    ]
    useFAQSchema({ faqs })

    // The preview only ever holds an object URL for the local file — nothing is
    // sent anywhere. Revoke the previous one whenever it is replaced so the blob
    // does not leak for the lifetime of the tab.
    useEffect(() => {
        return () => {
            if (mediaUrl) URL.revokeObjectURL(mediaUrl)
        }
    }, [mediaUrl])

    const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) loadFile(file)
    }

    const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
        e.preventDefault()
        setDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (file) loadFile(file)
    }

    const loadFile = (file: File) => {

        setFileError(null)

        if (file.size > MAX_FILE_BYTES) {
            setFileError(t('tools.pages.postPreview.fileTooLarge'))
            return
        }
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
            setFileError(t('tools.pages.postPreview.unsupportedFormat'))
            return
        }

        setMediaUrl((previous) => {
            if (previous) URL.revokeObjectURL(previous)
            return URL.createObjectURL(file)
        })
        setMediaKind(file.type.startsWith('video/') ? 'video' : 'image')
        setFileInfo({ name: file.name, size: file.size })
    }

    const clearMedia = () => {
        setMediaUrl((previous) => {
            if (previous) URL.revokeObjectURL(previous)
            return null
        })
        setFileError(null)
        setFileInfo(null)
        if (inputRef.current) inputRef.current.value = ''
    }

    const hashtags = useMemo(() => parseHashtags(hashtagInput), [hashtagInput])
    // People type their handle with the @; the previews add their own.
    const displayName = username.trim().replace(/^@+/, '') || t('tools.pages.postPreview.usernamePlaceholder')

    // Hashtags live inside the caption on every one of these platforms, so the
    // character count has to include them or the tool would under-report.
    const hashtagText = hashtags.map((tag) => `#${tag}`).join(' ')
    const totalLength = caption.length + (hashtagText ? hashtagText.length + 1 : 0)

    const previewProps = {
        mediaUrl: mediaUrl ?? undefined,
        caption: caption || undefined,
        hashtags: hashtags.length > 0 ? hashtags : undefined,
        username: displayName,
    }

    // Instagram renders 1:1 for feed photos and 9:16 for Reels. Without this the
    // component always falls back to Reels, which would misrepresent an image post.
    const instagramContentType = mediaKind === 'image' ? 'photo' : 'reels'
    const instagramLabel = mediaKind === 'image' ? t('tools.pages.postPreview.instagramFeed') : t('tools.pages.postPreview.instagramReels')

    const emptyLabel = t('tools.pages.postPreview.emptyPreview')
    const previews: { id: PreviewId; label: string; node: React.ReactNode }[] = [
        {
            id: 'instagram',
            label: instagramLabel,
            node: <InstagramPreview {...previewProps} mediaKind={mediaKind} contentType={instagramContentType} emptyLabel={emptyLabel} />,
        },
        { id: 'tiktok', label: t('board.platforms.tiktok'), node: <TikTokPreview {...previewProps} mediaKind={mediaKind} emptyLabel={emptyLabel} /> },
        { id: 'shorts', label: 'YouTube Shorts', node: <YouTubeShortsPreview {...previewProps} mediaKind={mediaKind} emptyLabel={emptyLabel} /> },
    ]

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
            <div className="flex-grow">
                <PublicHeader />

                {/* Hero */}
                <section className="pt-32 pb-10 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <ToolBreadcrumb path="/tools/post-preview" />
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            {t('tools.pages.postPreview.heading')}
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-400">
                            {t('tools.pages.postPreview.subtitle')}
                        </p>
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-500">
                            {t('tools.pages.postPreview.privacyNote')}
                        </p>
                    </div>
                </section>

                {/* Tool */}
                <section className="pb-20 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-[380px_1fr]">
                        {/* Controls */}
                        <div className="space-y-5">
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 space-y-5">
                                {/* Media */}
                                <div className="space-y-2">
                                    <span id="post-preview-media-label" className={TOOL_LABEL}>
                                        {t('tools.pages.postPreview.media')}
                                    </span>
                                    {/* sr-only rather than display:none, so the drop zone's
                                        input stays reachable with Tab and Space. */}
                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept="image/*,video/*"
                                        onChange={handleFile}
                                        className="peer sr-only"
                                        id="post-preview-file"
                                        aria-labelledby="post-preview-media-label"
                                        aria-describedby={fileError ? 'post-preview-file-error' : undefined}
                                    />
                                    {mediaUrl ? (
                                        <div className="flex items-center gap-3 p-2 pr-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
                                            {mediaKind === 'image' ? (
                                                <img src={mediaUrl} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover bg-gray-200 dark:bg-gray-700" />
                                            ) : (
                                                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white">
                                                    <FilmIcon className="w-5 h-5" />
                                                </span>
                                            )}
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-gray-800 dark:text-gray-200" title={fileInfo?.name}>
                                                    {fileInfo?.name ?? (mediaKind === 'video' ? t('tools.pages.postPreview.videoLoaded') : t('tools.pages.postPreview.imageLoaded'))}
                                                </span>
                                                {fileInfo && (
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                                                        {formatFileSize(fileInfo.size, i18n.language)}
                                                    </span>
                                                )}
                                            </span>
                                            <label
                                                htmlFor="post-preview-file"
                                                className="cursor-pointer rounded text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                                            >
                                                {t('tools.pages.postPreview.replace')}
                                            </label>
                                            <button
                                                type="button"
                                                onClick={clearMedia}
                                                className="rounded text-sm text-gray-500 hover:text-red-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                            >
                                                {t('tools.pages.postPreview.remove')}
                                            </button>
                                        </div>
                                    ) : (
                                        <label
                                            htmlFor="post-preview-file"
                                            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                                            onDragLeave={() => setDragging(false)}
                                            onDrop={handleDrop}
                                            className={`flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-800 ${dragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500'}`}
                                        >
                                            <ArrowUpTrayIcon className="w-6 h-6 text-gray-400" />
                                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                                {t('tools.pages.postPreview.chooseMedia')}
                                            </span>
                                            <span className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">{t('tools.pages.postPreview.dropHint')}</span>
                                            <span className="text-xs text-gray-400">{t('tools.pages.postPreview.maxSize')}</span>
                                        </label>
                                    )}
                                    {fileError && (
                                        <p id="post-preview-file-error" role="alert" className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
                                            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                                            {fileError}
                                        </p>
                                    )}
                                </div>

                                {/* Username */}
                                <div className="space-y-2">
                                    <label htmlFor="post-preview-username" className={TOOL_LABEL}>
                                        {t('tools.pages.postPreview.username')}
                                    </label>
                                    <input
                                        id="post-preview-username"
                                        type="text"
                                        autoComplete="off"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder={t('tools.pages.postPreview.usernamePlaceholder')}
                                        className={TOOL_INPUT}
                                    />
                                </div>

                                {/* Caption */}
                                <div className="space-y-2">
                                    <label htmlFor="post-preview-caption" className={TOOL_LABEL}>
                                        {t('tools.pages.postPreview.caption')}
                                    </label>
                                    <textarea
                                        id="post-preview-caption"
                                        value={caption}
                                        onChange={(e) => setCaption(e.target.value)}
                                        rows={5}
                                        placeholder={t('tools.pages.postPreview.captionPlaceholder')}
                                        className={`${TOOL_INPUT} py-3 resize-y`}
                                    />
                                </div>

                                {/* Hashtags */}
                                <div className="space-y-2">
                                    <label htmlFor="post-preview-hashtags" className={TOOL_LABEL}>
                                        {t('tools.pages.postPreview.hashtag')}
                                    </label>
                                    <input
                                        id="post-preview-hashtags"
                                        type="text"
                                        autoComplete="off"
                                        value={hashtagInput}
                                        onChange={(e) => setHashtagInput(e.target.value)}
                                        placeholder={t('tools.pages.postPreview.hashtagPlaceholder')}
                                        aria-describedby="post-preview-hashtags-hint"
                                        className={TOOL_INPUT}
                                    />
                                    <p id="post-preview-hashtags-hint" className={TOOL_HINT}>
                                        {t('tools.pages.postPreview.hashtagHint')}
                                        {hashtags.length > 0 && ` ${t('tools.pages.postPreview.hashtagsDetected', { count: hashtags.length })}`}
                                    </p>
                                </div>
                            </div>

                            {/* Limit checker */}
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                                    {t('tools.pages.postPreview.limitsHeading')}
                                </h2>
                                <div className="space-y-4">
                                    {PLATFORM_ORDER.map((id) => (
                                        <LimitRow
                                            key={id}
                                            limit={PLATFORM_LIMITS[id]}
                                            totalLength={totalLength}
                                            hashtagCount={hashtags.length}
                                        />
                                    ))}
                                </div>
                                <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
                                    {t('tools.pages.postPreview.limitsNote')}
                                </p>
                            </div>
                        </div>

                        {/* Previews: sticky on desktop so they stay beside the caption while typing. */}
                        <div className="lg:sticky lg:top-28 lg:self-start">
                            <div
                                role="group"
                                aria-label={t('tools.pages.postPreview.previewSwitcher')}
                                className="sm:hidden mb-4 grid grid-cols-3 gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1"
                            >
                                {previews.map((preview) => (
                                    <button
                                        key={preview.id}
                                        type="button"
                                        aria-pressed={activePreview === preview.id}
                                        onClick={() => setActivePreview(preview.id)}
                                        className={`rounded-lg px-2 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${activePreview === preview.id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
                                    >
                                        {preview.label}
                                    </button>
                                ))}
                            </div>
                            <div className="grid gap-4 sm:grid-cols-3 items-start">
                                {previews.map((preview) => (
                                    <div
                                        key={preview.id}
                                        className={`${activePreview === preview.id ? 'block' : 'hidden'} sm:block mx-auto w-full max-w-[300px] sm:max-w-none`}
                                    >
                                        <p className="hidden sm:block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 text-center">
                                            {preview.label}
                                        </p>
                                        {preview.node}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Path to product */}
                <section className="pb-24 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-8 sm:p-10">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {t('tools.pages.postPreview.ctaHeading')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {t('tools.pages.postPreview.ctaBody')}
                        </p>
                        <Link to="/register" className={buttonClassName({ className: 'shadow-lg shadow-primary-500/50 group' })}>
                            {t('tools.pages.postPreview.ctaButton')}
                            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>

                    <ToolFAQSection heading={t('tools.pages.postPreview.faqHeading')} faqs={faqs} />
                </section>

                <MoreTools currentPath="/tools/post-preview" />
                <div className="pb-20" />
            </div>

            <LandingFooter />
        </div>
    )
}

function LimitRow({
    limit,
    totalLength,
    hashtagCount,
}: {
    limit: PlatformLimit
    totalLength: number
    hashtagCount: number
}) {
    const { t, i18n } = useTranslation()
    const overLimit = totalLength > limit.hardLimit
    const truncated = totalLength > limit.truncateAt
    const tooManyHashtags = limit.maxHashtags !== null && hashtagCount > limit.maxHashtags
    const percent = Math.min(100, (totalLength / limit.hardLimit) * 100)

    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{limit.label}</span>
                <span
                    className={`text-xs font-mono ${overLimit ? 'text-red-500 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}
                >
                    {totalLength.toLocaleString(i18n.language)} / {limit.hardLimit.toLocaleString(i18n.language)}
                </span>
            </div>

            <div aria-hidden="true" className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all ${overLimit ? 'bg-red-500' : truncated ? 'bg-amber-500' : 'bg-primary-500'}`}
                    style={{ width: `${percent}%` }}
                />
            </div>

            <div className="mt-1.5 space-y-0.5">
                {overLimit && (
                    <p className="text-xs text-red-500">
                        {t('tools.pages.postPreview.overLimit', {
                            platform: limit.label,
                            excess: (totalLength - limit.hardLimit).toLocaleString(i18n.language),
                        })}
                    </p>
                )}
                {!overLimit && truncated && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                        {t('tools.pages.postPreview.truncated', { chars: limit.truncateAt })}
                    </p>
                )}
                {tooManyHashtags && (
                    <p className="text-xs text-red-500">
                        {t('tools.pages.postPreview.tooManyHashtags', {
                            used: hashtagCount,
                            max: limit.maxHashtags,
                            note: t(limit.noteKey),
                        })}
                    </p>
                )}
            </div>
        </div>
    )
}
